/**
 * Grand Quack Auto — fixed-timestep game loop.
 *
 * This is the spine of the codebase. Get it wrong here and every future physics
 * bug (tunneling, input drift, tab-resume explosions) starts here.
 *
 * Pattern: "Fix Your Timestep" accumulator with a MAX_FRAME_DT spiral-of-death cap.
 *  - fixedUpdate runs 0+ times per render frame at exactly FIXED_DT seconds
 *    — physics, gameplay, input-consuming logic go here.
 *  - render runs exactly once per frame with alpha ∈ [0,1]
 *    — camera lerps, visual smoothing between physics ticks go here.
 *
 * Systems are registered once at boot via registerSystem() and called in
 * registration order every frame. System order matters: register input before
 * movement before physics before camera.
 *
 * See research.md §5 .cursorrules "Physics" section.
 */

import { FIXED_DT, MAX_FRAME_DT } from './config';
import type { System, World } from './types';

const systems: System[] = [];
let accumulator = 0;
let lastTime = 0;
let running = false;

export function registerSystem(system: System): void {
  systems.push(system);
  console.log(`[gameLoop] registered system: ${system.name}`);
}

export function listSystems(): readonly string[] {
  return systems.map((s) => s.name);
}

/**
 * Start the render loop. Idempotent — calling twice does nothing.
 * Systems must be registered BEFORE calling this.
 */
export function startGameLoop(world: World): void {
  if (running) {
    console.warn('[gameLoop] already running — ignoring startGameLoop call');
    return;
  }
  running = true;
  lastTime = performance.now() / 1000;
  console.log(`[gameLoop] starting with ${systems.length} systems`);

  const frame = (nowMs: number): void => {
    const nowSeconds = nowMs / 1000;
    const rawDt = nowSeconds - lastTime;
    // Spiral-of-death cap: if we were paused in a debugger or the tab was
    // backgrounded, don't try to simulate 10,000 physics ticks at once.
    const frameDt = Math.min(rawDt, MAX_FRAME_DT);
    lastTime = nowSeconds;
    world.time.lastFrame = frameDt;

    accumulator += frameDt * world.time.timeScale;
    while (accumulator >= FIXED_DT) {
      for (const system of systems) {
        system.fixedUpdate?.(FIXED_DT, world);
      }
      accumulator -= FIXED_DT;
      world.time.elapsed += FIXED_DT;
    }

    // alpha is the fractional progress between the last fixedUpdate and the
    // next — used by render hooks for smooth interpolation.
    const alpha = accumulator / FIXED_DT;
    for (const system of systems) {
      system.render?.(frameDt, alpha, world);
    }

    if (world.composer) world.composer.render();
    else world.renderer.render(world.scene, world.camera);
    requestAnimationFrame(frame);
  };

  requestAnimationFrame(frame);
}
